import React from "react";
import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://omnivaas.com";
export const SITE_NAME = "OmNivaas Properties";
export const SITE_LOGO = `${SITE_URL}/images/omnivaas-logo-256.jpg`;
export const FAVICON_VERSION = "20260708";
export const DEFAULT_SEO_IMAGE =
  "https://images.unsplash.com/photo-1680034733365-ad7263988417?w=1200&h=630&fit=crop";

export function getCanonicalUrl(path = "/") {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;
}

function normalizeKeywords(keywords = []) {
  if (!keywords) {
    return [];
  }

  if (typeof keywords === "string") {
    return keywords
      .split(",")
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  }

  return keywords.map((keyword) => String(keyword).trim()).filter(Boolean);
}

export function createWebPageJsonLd({ title, description, path = "/", keywords = [] }) {
  const url = getCanonicalUrl(path);
  const normalizedKeywords = normalizeKeywords(keywords);

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    ...(normalizedKeywords.length
      ? { keywords: normalizedKeywords.join(", ") }
      : {}),
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export function createBreadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getCanonicalUrl(item.path),
    })),
  };
}

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: SITE_LOGO,
  sameAs: [],
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/properties?societyName={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

function SEO({
  title,
  description,
  path = "/",
  canonical,
  robots = "index,follow",
  type = "website",
  image = DEFAULT_SEO_IMAGE,
  imageAlt = SITE_NAME,
  keywords = [],
  jsonLd = [],
  children,
}) {
  const canonicalUrl = canonical || getCanonicalUrl(path);
  const schemas = jsonLd.filter(Boolean);
  const twitterCard = image ? "summary_large_image" : "summary";
  const normalizedKeywords = normalizeKeywords(keywords);
  const keywordContent = normalizedKeywords.join(", ");

  return (
    <Helmet>
      <html lang="en-IN" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywordContent && <meta name="keywords" content={keywordContent} />}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="geo.region" content="IN" />
      <link rel="icon" type="image/svg+xml" href={`/favicon.svg?v=${FAVICON_VERSION}`} />
      <link rel="icon" type="image/png" sizes="32x32" href={`/favicon-32x32.png?v=${FAVICON_VERSION}`} />
      <link rel="icon" type="image/png" sizes="16x16" href={`/favicon-16x16.png?v=${FAVICON_VERSION}`} />
      <link rel="shortcut icon" href={`/favicon.ico?v=${FAVICON_VERSION}`} />
      <link rel="apple-touch-icon" sizes="180x180" href={`/apple-touch-icon.png?v=${FAVICON_VERSION}`} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-IN" href={canonicalUrl} />
      {children}
      <meta property="og:locale" content="en_IN" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:alt" content={imageAlt} />}
      {image && <meta property="og:image:width" content="1200" />}
      {image && <meta property="og:image:height" content="630" />}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      {image && <meta name="twitter:image:alt" content={imageAlt} />}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
